import { FC, useCallback, useState } from 'react';
import { Box, Skeleton, SxProps, Theme } from '@mui/material';
import { DeviceProfile } from '@/types/pages/storeManagement/device';
import { getPreviewUrl, getProfileSize, hasPreviewImage } from '@/utils/deviceDetailUtlis';

type PreviewImageProps = {
    imgPath?: string | null;
    alt: string;
    profile?: DeviceProfile | null;
    maxWidth: number;
    maxHeight: number;
    sx?: SxProps<Theme>;
};

const PreviewImage: FC<PreviewImageProps> = ({ imgPath, alt, profile, maxWidth, maxHeight, sx }) => {
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);

    // 先用路徑字串判斷「預期是否有預覽圖」,避免對空路徑發請求；
    // 但若實際載入失敗(onError),即使路徑存在也視為沒有預覽圖,改用 contain 顯示 fallback 圖示。
    const previewAvailable = hasPreviewImage(imgPath) && !failed;
    const src = getPreviewUrl(previewAvailable ? (imgPath as string) : '');

    const { width, height } = getProfileSize(profile);
    const scale = Math.min(1, maxWidth / width, maxHeight / height);
    const boxWidth = Math.round(width * scale);
    const boxHeight = Math.round(height * scale);

    const handleOnLoad = useCallback(() => {
        setLoaded(true);
    }, []);
    const handleOnError = useCallback(() => {
        setLoaded(true);
        setFailed(true);
    }, []);

    return (
        <Box
            sx={{
                position: 'relative',
                width: boxWidth,
                height: boxHeight,
                aspectRatio: `${boxWidth} / ${boxHeight}`,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                ...sx,
            }}
        >
            {!loaded && (
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ position: 'absolute', inset: 0 }} />
            )}

            <Box
                component="img"
                src={src}
                alt={alt}
                onLoad={handleOnLoad}
                onError={handleOnError}
                sx={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: previewAvailable ? 'contain' : 'cover',
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                }}
            />
        </Box>
    );
};

export default PreviewImage;
